'use client'

import type { TAbsoluteLocale, TCountryStandard } from '@src/config/localisation/locale-config.types'
import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'
import { ANALYTIC_COOKIES, USER_CONSENT_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getQueryParam } from '@src/quince-site-common/src/utils/get-query-param'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { getStandardLocale, parseLocale } from '@src/utils/i18n.utils'

import { STATSIG_EXPERIMENTS_CONFIG } from '../../config/statsig-experiments.config'

import { buildStatsigCustom, parseAppVersionFromUserAgent, StatsigAppVersion } from './statsig-consent'
import { StatsigUserWithStandardLocale } from './statsig.types'

export const getStatsigUser = (locale: TAbsoluteLocale): StatsigUserWithStandardLocale | null => {
  const userId = getlbUserId()
  if (!userId) return null
  const email = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)
  const user: StatsigUserWithStandardLocale = {
    userID: userId,
    custom: buildStatsigCustom({
      isCountryCompliant: getIsCountryCompliant(),
      forcedBuckets: getForcedBucketsForUser(),
      consentCookieValue: getCookie(USER_CONSENT_COOKIES.FUNCTIONAL),
      appVersion: getClientAppVersion(),
    }) as StatsigUserWithStandardLocale['custom'],
    locale: getStandardLocale(locale),
    country: parseLocale(locale).country.toUpperCase() as TCountryStandard,
    ...(email && { email }),
  }
  return user
}

/**
 * Client-path parity with the server bootstrap: parse the app version from `navigator.userAgent`
 * (the same UA token the server reads) and pass it to `buildStatsigCustom` as `app_version` /
 * `app_ota_version`. Returns `undefined` off-browser and for web/mWeb (no app token in the UA).
 */
const getClientAppVersion = (): StatsigAppVersion | undefined => {
  if (!isBrowser()) return undefined
  return parseAppVersionFromUserAgent(navigator.userAgent)
}

const LOCALSTORAGE_FORCEBUCKET_KEY = 'statsigForcedBuckets'

// Shared helper to build forcedBuckets from a mapping of experimentName -> bucket
function buildForcedBuckets(experimentToBucket: Record<string, string | undefined>): Record<string, string[]> {
  const forcedBuckets: Record<string, string[]> = {}
  Object.entries(experimentToBucket).forEach(([experimentName, bucket]) => {
    if (bucket) {
      forcedBuckets[bucket] ??= []
      forcedBuckets[bucket].push(experimentName)
    }
  })
  return forcedBuckets
}

export const getForcedBucketsForUser = () => {
  try {
    if (!isBrowser()) return {}

    // template { experimentName: bucketName }
    const persistedForcedBuckets = JSON.parse(localStorage.getItem(LOCALSTORAGE_FORCEBUCKET_KEY) ?? '{}') as Record<
      string,
      string
    >

    const experimentToBucket: Record<string, string | undefined> = {}
    const persistedValues: Record<string, string> = {}

    Object.values(STATSIG_EXPERIMENTS_CONFIG).forEach(experimentConfig => {
      let bucket = getQueryParam(experimentConfig.experimentName)
      bucket ??= persistedForcedBuckets[experimentConfig.experimentName]
      if (bucket) {
        persistedValues[experimentConfig.experimentName] = bucket
        experimentToBucket[experimentConfig.experimentName] = bucket
      }
    })

    localStorage.setItem(LOCALSTORAGE_FORCEBUCKET_KEY, JSON.stringify(persistedValues))
    return buildForcedBuckets(experimentToBucket)
  } catch (error) {
    console.error(error)
    return {}
  }
}

// SSR version: get forced buckets from query string only (no localStorage or window)
export function getServerSideStatsigForcedBuckets(queryString: string): Record<string, string[]> {
  const params = new URLSearchParams(queryString.startsWith('?') ? queryString : `?${queryString}`)
  const experimentToBucket: Record<string, string | undefined> = {}
  Object.values(STATSIG_EXPERIMENTS_CONFIG).forEach(experimentConfig => {
    const experimentName = experimentConfig.experimentName
    const bucket = params.get(experimentName)
    if (bucket) {
      experimentToBucket[experimentName] = bucket
    }
  })
  return buildForcedBuckets(experimentToBucket)
}
